import { forwardRef, useEffect, useRef } from 'react';
import {
  Animated,
  Platform,
  Pressable,
  type PressableProps,
  type StyleProp,
  StyleSheet,
  type View,
  type ViewStyle,
} from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';

type ToggleSize = 'sm' | 'md';

interface TrackDims {
  width: number;
  height: number;
  thumb: number;
  pad: number;
}

const TRACK_DIMS: Record<ToggleSize, TrackDims> = {
  sm: { width: 36, height: 20, thumb: 16, pad: 2 },
  md: { width: 44, height: 24, thumb: 20, pad: 2 },
};

export interface ToggleProps extends Omit<PressableProps, 'onPress' | 'style' | 'children'> {
  value: boolean;
  onValueChange: (next: boolean) => void;
  disabled?: boolean;
  size?: ToggleSize;
  style?: StyleProp<ViewStyle>;
}

export const Toggle = forwardRef<View, ToggleProps>(function Toggle(
  {
    value,
    onValueChange,
    disabled = false,
    size = 'md',
    accessibilityLabel,
    accessibilityHint,
    style,
    ...rest
  },
  ref,
) {
  const dims = TRACK_DIMS[size];
  const translate = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(translate, {
      toValue: value ? 1 : 0,
      duration: tokens.duration.fast,
      useNativeDriver: true,
    }).start();
  }, [value, translate]);

  const translateX = translate.interpolate({
    inputRange: [0, 1],
    outputRange: [dims.pad, dims.width - dims.thumb - dims.pad],
  });

  const handlePress = () => {
    if (disabled) return;
    onValueChange(!value);
  };

  const trackColor = value ? colors.primary[500] : colors.background.secondary;
  const trackBorder = value ? colors.primary.borderActive : colors.border.subtle;

  return (
    <Pressable
      ref={ref}
      onPress={handlePress}
      disabled={disabled}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      hitSlop={8}
      style={[
        styles.track,
        {
          width: dims.width,
          height: dims.height,
          borderRadius: dims.height / 2,
          backgroundColor: trackColor,
          borderColor: trackBorder,
          opacity: disabled ? 0.5 : 1,
        },
        style,
      ]}
      {...rest}
    >
      <Animated.View
        style={[
          styles.thumb,
          {
            width: dims.thumb,
            height: dims.thumb,
            borderRadius: dims.thumb / 2,
            top: (dims.height - dims.thumb) / 2 - StyleSheet.hairlineWidth,
            transform: [{ translateX }],
          },
        ]}
      />
    </Pressable>
  );
});

const styles = StyleSheet.create({
  track: {
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
    ...Platform.select({
      web: {
        cursor: 'pointer' as unknown as undefined,
        transition: 'background-color 170ms ease, border-color 170ms ease',
      } as ViewStyle,
      default: {},
    }),
  },
  thumb: {
    position: 'absolute',
    left: 0,
    backgroundColor: colors.foreground,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
      },
      android: { elevation: 2 },
      web: {
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)' as unknown as undefined,
      } as ViewStyle,
    }),
  },
});
